import { useCallback } from "react";
import { API_BASE_URL } from "../constants/app.constant";
import { useFetch } from "../hooks/useFetch";
import { ConversationType } from "../constants/chat.constant";
import { APIResponse } from "../interfaces/common.interface";
import {
  ChatConversationDetail,
  ChatFolderDetail,
  ChatSessionData,
  ChopGptPayload,
  ChopGptResponse,
  Feedback,
  PresignedUrl,
} from "../interfaces/chat.interface";
import useAuthService from "./auth.service";

/**
 * useChatApiService hook provides chat-related API service functions.
 * Handles conversation management, message streaming, feedback, and presigned URLs.
 * 
 * @hook
 * @returns {Object} Chat API service methods
 * @returns {Function} returns.getAllChatFolders - Get all chat folders/conversations
 * @returns {Function} returns.getChatConversation - Get specific conversation messages
 * @returns {Function} returns.deleteChatConversation - Delete a conversation
 * @returns {Function} returns.getChopGptResponse - Stream AI response via Web Worker
 * @returns {Function} returns.updateMessageFeedback - Update message feedback
 * @returns {Function} returns.getPresignedUrl - Get presigned URL for file operations
 * @returns {Function} returns.getStreamWorker - Create Web Worker for streaming
 * 
 * @example
 * ```tsx
 * const { getAllChatFolders, getChatConversation } = useChatApiService();
 * 
 * const folders = await getAllChatFolders(ConversationType.CHOP_GPT);
 * const conversation = await getChatConversation('session-id', ConversationType.CHOP_GPT);
 * ```
 */
const useChatApiService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();
  const CHAT_API_BASE_URL = API_BASE_URL + "/chat";
  const CHAT_SESSION_API_BASE_URL = CHAT_API_BASE_URL + "/sessions";

  /**
   * Gets all chat folders/conversations for a conversation type.
   * 
   * @param {ConversationType | null} conversationType - Type of conversation, null for all types
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<Array<ChatFolderDetail>>>} Array of chat folders
   * @throws {Error} If token is invalid
   */
  const getAllChatFolders = useCallback(
    async (
      conversationType: ConversationType | null = null,
      signal?: AbortSignal
    ) => {
      // Get current user access token
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      let url = CHAT_SESSION_API_BASE_URL;
      if (conversationType) {
        url = url + `?type=${conversationType}`;
      }

      return fetchService.get<APIResponse<Array<ChatFolderDetail>>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      });
    },
    [fetchService]
  );

  /**
   * Gets a specific conversation with all its messages.
   * 
   * @param {string} conversationId - Conversation session ID
   * @param {ConversationType} conversationType - Type of conversation
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<ChatSessionData>>} Conversation data with messages
   * @throws {Error} If token is invalid
   */
  const getChatConversation = useCallback(
    async (
      conversationId: string,
      conversationType: ConversationType,
      signal?: AbortSignal
    ) => {
      // Get current user access token
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      return fetchService.get<APIResponse<ChatSessionData>>(
        `${CHAT_SESSION_API_BASE_URL}/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );
    },
    [fetchService]
  );


  /**
   * Deletes a chat conversation.
   * 
   * @param {string} conversationId - Conversation session ID to delete
   * @param {ConversationType} conversationType - Type of conversation
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<string>>} Deletion confirmation
   * @throws {Error} If token is invalid
   */
  const deleteChatConversation = useCallback(
    async (
      conversationId: string,
      conversationType: ConversationType,
      signal?: AbortSignal
    ) => {
      // Get current user access token
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      return fetchService.delete<APIResponse<string>>(
        `${CHAT_SESSION_API_BASE_URL}/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );
    },
    [fetchService]
  );

  const get_reasoning_effort = (effort: string, model: string) => {
    switch (effort) {
      case '0':
        return model=="gpt-5.6-terra"?'low': "minimal";
      case '2':
        return model=="gpt-5.6-terra"?'medium': "low";
      case '4':
        return model=="gpt-5.6-terra"?'high': "medium";
      case '6':
        return model=="gpt-5.6-terra"?'xhigh': "high";
      default:
        return model=="gpt-5.6-terra"?'low': 'minimal';
    }
  }

  /**
   * Initiates streaming AI response via Web Worker.
   * Posts message to worker with URL, token, and payload for streaming.
   * 
   * @param {ChopGptPayload} payload - Chat request payload (conversationId, model, temperature, etc.)
   * @param {Worker} worker - Web Worker instance for handling stream
   * @returns {Promise<void>} Promise that resolves when streaming starts
   * @throws {Error} If token is invalid
   */
  const getChopGptResponse = useCallback(
    async (
      payload: ChopGptPayload,
      worker: any
    ): Promise<void> => {
      const token = await getUserAccessToken()
      if (!token) throw new Error("No authentication token");

      const url = `${API_BASE_URL}/chat/${payload.conversationId}/streaming`;

      const reasoning_effort = get_reasoning_effort(payload.effort, payload.model)

      worker.postMessage({
        url,
        token,
        payload: {
          conversationId: payload.conversationId,
          conversationType: payload.conversationType,
          model: payload.model,
          temperature: payload.temperature,
          effort: reasoning_effort,
          message: payload.message,
          files: payload.files,
          sensitive: payload.sensitive || false,
        },
      });

    },
    [getUserAccessToken]
  );

  /**
   * Updates feedback for a specific message in a conversation.
   * 
   * @param {string} sessionId - Conversation session ID
   * @param {number} messageId - Message ID to update feedback for
   * @param {Feedback} request - Feedback data (positive/negative)
   * @returns {Promise<APIResponse<ChatSessionData>>} Updated conversation data
   * @throws {Error} If token is invalid
   */
  const updateMessageFeedback = useCallback(
    async (sessionId: string, messageId: number, request: Feedback) => {
      // Get current user access token
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      return fetchService.put<APIResponse<ChatSessionData>>(
        `${CHAT_SESSION_API_BASE_URL}/${sessionId}/message/${messageId}/feedback`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: request,
        }
      );
    },
    [fetchService]
  );


  /**
   * Gets a presigned URL for file operations (upload/download).
   * 
   * @param {string} [type="uploads"] - Type of presigned URL (uploads, policy, etc.)
   * @param {PresignedUrl} payload - Presigned URL request payload (blob_path, expiry_hours, permission)
   * @param {boolean} [_regenerate=false] - Whether to regenerate URL (currently unused)
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<{presigned_url: string; expires_in_hours: number}>} Presigned URL and expiry info
   * @throws {Error} If token is invalid
   */
  const getPresignedUrl = useCallback(
    async (
      type: string = "uploads",
      payload: PresignedUrl,
      _regenerate: boolean = false,
      signal?: AbortSignal
    ): Promise<{ presigned_url: string; expires_in_hours: number }> => {
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      const response = await fetchService.post<APIResponse<{ presigned_url: string; expires_in_hours: number }>>(
        `${API_BASE_URL}/commons/presign-url/${type}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: payload,
          signal,
        }
      );

      return response.data;
    },
    [fetchService]
  );

  /**
   * Creates a new Web Worker instance for handling chat stream responses.
   * 
   * @returns {Worker} New Web Worker instance for streaming
   */
  const getStreamWorker = (): Worker => {
    const worker = new Worker(new URL("./chat-stream-worker.service.ts", import.meta.url), {
      type: "module",
    });
    return worker;
  };
  return {
    getAllChatFolders,
    getChatConversation,
    getChopGptResponse,
    deleteChatConversation,
    getPresignedUrl,
    updateMessageFeedback,
    getStreamWorker
  };
};

export default useChatApiService;
